import { getLogger } from '@jitsi/logger';

const logger = getLogger(__filename);

/**
 * Moving a node into another `Document` adopts it, and adoption is not free: the browser tears down and reloads
 * every `<iframe>` in the moved subtree, and it pauses media elements. Document Picture-in-Picture moves the whole
 * application root in on open and back out on close, so anything with playback state in it is reset twice per PiP
 * session - which is what made a shared video restart from the beginning, and start playing again even when the
 * user had paused it.
 *
 * These helpers snapshot the state adoption destroys and put it back afterwards. They cannot preserve what lives
 * inside a reloaded iframe - that is the browser's behaviour and there is no hook to prevent it - but they do keep
 * the frame pointed at the same document instead of a blank one, and they keep media elements where the user left
 * them rather than rewound and playing.
 */

/**
 * Records the state that a cross-document move would otherwise discard.
 *
 * @param {Element} root - The subtree about to be adopted.
 * @returns {Object} A snapshot to hand to {@link restoreAfterAdoption}.
 */
export function snapshotBeforeAdoption(root) {
    const media = [];
    const frames = [];

    if (!root || typeof root.querySelectorAll !== 'function') {
        return {
            media,
            frames
        };
    }

    try {
        for (const el of root.querySelectorAll('video, audio')) {
            media.push({
                el,

                // `srcObject` is how every conference stream is attached; `src` only shows up on ordinary media.
                srcObject: el.srcObject || null,
                src: el.srcObject ? '' : el.getAttribute('src') || '',
                currentTime: Number.isFinite(el.currentTime) ? el.currentTime : 0,

                // Read `paused` rather than inferring it - a paused element that gets re-attached and autoplayed
                // is precisely the reported bug.
                wasPlaying: !el.paused && !el.ended,
                muted: el.muted
            });
        }

        for (const el of root.querySelectorAll('iframe')) {
            frames.push({
                el,

                // Read the attribute, not the property: after adoption the property reads as the blank document
                // the browser reloaded into, so the original would already be gone by restore time.
                src: el.getAttribute('src') || ''
            });
        }
    } catch (error) {
        logger.warn('Could not snapshot subtree before adoption', error);
    }

    return {
        media,
        frames
    };
}

/**
 * Puts back what the move discarded. Safe to call when nothing was actually lost - every restore is guarded by a
 * check that the value really did change.
 *
 * @param {Object} snapshot - The value returned by {@link snapshotBeforeAdoption}.
 * @returns {void}
 */
export function restoreAfterAdoption(snapshot) {
    if (!snapshot) {
        return;
    }

    const { media = [], frames = [] } = snapshot;

    for (const entry of frames) {
        const { el, src } = entry;

        if (!src || !el.isConnected) {
            continue;
        }

        const current = el.getAttribute('src') || '';

        // Only rewrite a frame the adoption actually blanked. Re-assigning a src that is already correct would
        // force a second, avoidable reload.
        if (current !== src) {
            logger.debug('Restoring iframe src lost to document adoption');
            el.setAttribute('src', src);
        }
    }

    for (const entry of media) {
        const { el, srcObject, src, currentTime, wasPlaying, muted } = entry;

        if (!el.isConnected) {
            continue;
        }

        // Adoption pauses media but keeps srcObject, so an element now showing another stream was re-pointed by the
        // app after the snapshot - the large video moving to the next speaker while the deferred pass waited for a
        // frame, which in a background tab is until the user comes back. The snapshot no longer describes it, and
        // restoring would put the previous stream back behind the owner's back.
        if (srcObject && el.srcObject && el.srcObject !== srcObject) {
            continue;
        }

        try {
            if (srcObject && el.srcObject !== srcObject) {
                el.srcObject = srcObject;
            } else if (!srcObject && src && (el.getAttribute('src') || '') !== src) {
                el.setAttribute('src', src);
            }

            el.muted = muted;

            // Live conference streams have no meaningful seek position and setting one on them throws, so only
            // restore the playhead for ordinary media.
            if (!srcObject && Number.isFinite(currentTime) && currentTime > 0
                    && Math.abs(el.currentTime - currentTime) > 0.25) {
                el.currentTime = currentTime;
            }

            if (wasPlaying && el.paused) {
                const played = el.play();

                played?.catch?.(error => logger.debug('Could not resume media after adoption', error));
            } else if (!wasPlaying && !el.paused) {
                // Adoption can restart an element the user had deliberately paused.
                el.pause();
            }
        } catch (error) {
            logger.warn('Could not restore media element after adoption', error);
        }
    }
}

/**
 * Moves `node` into `target`, keeping the playback and frame state that the move would otherwise destroy.
 *
 * @param {Element} target - The element in the destination document to append into.
 * @param {Element} node - The node being moved across documents.
 * @returns {void}
 */
export function appendPreservingState(target, node) {
    if (!target || !node) {
        return;
    }

    const snapshot = snapshotBeforeAdoption(node);

    target.append(node);
    restoreAfterAdoption(snapshot);

    // Iframe reloads and the pause that comes with re-attaching a stream both land after the current task, so a
    // single synchronous pass above can be undone by the browser a moment later. Re-apply once the frame has been
    // painted; every restore is a no-op when nothing moved.
    const view = target.ownerDocument?.defaultView || window;

    view.requestAnimationFrame?.(() => restoreAfterAdoption(snapshot));
}
